import { Action } from "redux";

/**
 * Typ výsledkové akce, která může skončit úspěchem nebo chybou. Slouží jako základ pro konkrétní výsledkové akce.
 *
 * @template T Typ akce. Slouží pro rozlišení různých akcí. Běžně se získává z řetězcové konstanty pomocí typeof.
 * @template R Typ pro obsah akce v případě úspěchu.
 * @template E Typ pro obsah akce v případě chyby.
 *
 * @see Detaily použití v conventions/redux/actions.md.
 */
export interface IResultAction<T, R, E> extends Action<T> {
    readonly payload: R | E;
    readonly error: boolean;
    /** Meta v tomto případě slouží pouze pro účely TypeScript, aby zvládl odvodit typy R a E v pomocných typech. */
    readonly meta?: {
        readonly genericTypes?: {
            readonly resultType: R;
            readonly errorType: E;
        };
    };
}

/** Pomocný typ pro odvození typu generika T (typ akce) z typu IResultAction. */
type ExtractActionType<A> = A extends IResultAction<infer T, any, any> ? T : never;
/** Pomocný typ pro odvození typu generika R (typ obsahu akce při úspěchu) z typu IResultAction. */
type ExtractResultType<A> = A extends IResultAction<any, infer R, any> ? R : never;
/** Pomocný typ pro odvození typu generika E (typ obsahu akce při chybě) z typu IResultAction. */
type ExtractErrorType<A> = A extends IResultAction<any, any, infer E> ? E : never;

/**
 * Typ výsledkové akce zúžený na úspěch.
 *
 * @template A Typ výsledkové akce, který se zúží na úspěch.
 *
 * @see Detaily použití v conventions/redux/actions.md.
 */
export interface IResultActionSuccess<A> extends Action<ExtractActionType<A>> {
    readonly payload: ExtractResultType<A>;
    readonly error: false;
}

// eslint-disable-next-line unused-imports/no-unused-vars
export class IResultActionSuccess<A> {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && typeof instance.type === "string" && instance.error === false;
    }
}

/**
 * Typ výsledkové akce zúžený na chybu.
 *
 * @template A Typ výsledkové akce, který se zúží na chybu.
 *
 * @see Detaily použití v conventions/redux/actions.md.
 */
export interface IResultActionError<A> extends Action<ExtractActionType<A>> {
    readonly payload: ExtractErrorType<A>;
    readonly error: true;
}

// eslint-disable-next-line unused-imports/no-unused-vars
export class IResultActionError<A> {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && typeof instance.type === "string" && instance.error === true;
    }
}
